import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  Library,
  Compass,
  Sparkles,
  PlusCircle,
  CreditCard,
  BarChart3,
  Settings,
  Shield,
  FolderOpen,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, testid: "side-dashboard" },
  { to: "/quiz/ia", label: "Criar com IA", icon: Sparkles, testid: "side-ia" },
  { to: "/quiz/novo", label: "Novo quiz", icon: PlusCircle, testid: "side-novo" },
  { to: "/quizzes", label: "Meus quizzes", icon: Library, testid: "side-quizzes" },
  { to: "/biblioteca", label: "Biblioteca de quizzes", icon: Compass, testid: "side-biblioteca" },
  { to: "/atividades/gerenciar", label: "Atividades", icon: FolderOpen, testid: "side-atividades" },
  { to: "/resultados", label: "Resultados", icon: BarChart3, testid: "side-resultados" },
  { to: "/assinatura", label: "Minha assinatura", icon: CreditCard, testid: "side-assinatura" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = [...navItems];
  if (user?.role === "admin") {
    items.push({ to: "/admin", label: "Administração", icon: Shield, testid: "side-admin" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 h-16 flex items-center border-b border-slate-100">
        <Logo />
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {items.map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={item.testid}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                active
                  ? "bg-brand-blue text-white shadow-md shadow-blue-200"
                  : "text-brand-muted hover:bg-slate-100 hover:text-brand-ink"
              }`}
            >
              <Icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-purple to-brand-blue flex items-center justify-center text-white font-bold text-sm">
            {user?.name?.[0]?.toUpperCase() || "P"}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-ink truncate">{user?.name}</p>
            <p className="text-xs text-brand-muted truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg flex">
      <aside className="hidden lg:block w-72 bg-white border-r border-slate-100 fixed inset-y-0">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-white h-full">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 z-10 text-brand-muted"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-72">
        <header className="lg:hidden sticky top-0 z-40 h-16 bg-white/80 backdrop-blur-xl border-b border-slate-100 flex items-center justify-between px-5">
          <Logo />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6 text-brand-ink" />
          </button>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
